import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { getComicById, getRelatedComics } from '@/mocks/comics';
import type { Chapter } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(Number(id));
  const [currentChapter, setCurrentChapter] = useState<Chapter | null>(null);
  const [chapterPage, setChapterPage] = useState(1);

  if (!comic) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
          <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
        </div>
        <h2 className="text-xl font-bold text-foreground-900 mb-2">漫画未找到</h2>
        <p className="text-foreground-500 mb-6">该漫画不存在或已被移除</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const relatedComics = getRelatedComics(comic, 4);
  const totalChapterPages = 3;
  const totalChapters = comic.chapters.length;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩国漫画免费在线阅读`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.createdAt,
    dateModified: comic.updatedAt,
    image: comic.cover,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hanman.example.com/' },
      { '@type': 'ListItem', position: 2, name: comic.category, item: `https://hanman.example.com/category/${comic.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: comic.title },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${comic.title}可以在哪里免费阅读？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `您可以在韩漫网站上免费在线阅读《${comic.title}》。通过韩漫网址访问韩漫大全，搜索"${comic.title}"即可找到这部韩国漫画，无需付费即可享受完整阅读体验。`,
        },
      },
      {
        '@type': 'Question',
        name: `${comic.title}目前更新到多少话了？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `截至${comic.updatedAt}，《${comic.title}》已更新至第${totalChapters}话，目前状态为${comic.status}。您可以通过韩漫网站随时查看最新更新。`,
        },
      },
    ],
  };

  if (currentChapter) {
    return (
      <>
        <SEO
          title={`${comic.title} 第${currentChapter.number}话 - ${currentChapter.title}`}
          description={`在韩漫网站在线免费阅读《${comic.title}》第${currentChapter.number}话 ${currentChapter.title}。韩国漫画免费阅读，尽在韩漫大全。`}
          keywords={`韩漫网站,韩国漫画免费,${comic.title},第${currentChapter.number}话,${currentChapter.title}`}
          ogType="article"
          articlePublishedTime={comic.createdAt}
          articleModifiedTime={comic.updatedAt}
        />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

        <div className="max-w-3xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: comic.category, href: `/category/${comic.categorySlug}` },
              { label: comic.title, href: `/article/${comic.id}` },
              { label: `第${currentChapter.number}话` },
            ]}
          />

          <div className="bg-background-100 rounded-xl p-4 mb-6 flex items-center justify-between">
            <div>
              <h1 className="font-heading text-lg font-bold text-foreground-950">
                {comic.title} · 第{currentChapter.number}话
              </h1>
              <p className="text-sm text-foreground-500">{currentChapter.title}</p>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs text-foreground-500">第{chapterPage}/{totalChapterPages}页</span>
            </div>
          </div>

          <div className="bg-background-50 rounded-xl p-6 md:p-10 mb-4 min-h-[500px] flex flex-col items-center justify-center">
            <div className="text-center">
              <i className="ri-book-open-line text-5xl text-foreground-300 mb-4 block"></i>
              <h3 className="font-heading text-xl font-bold text-foreground-700 mb-2">
                {currentChapter.title}
              </h3>
              <p className="text-sm text-foreground-500 mb-8">
                第 {chapterPage} / {totalChapterPages} 页
              </p>
              <p className="text-xs text-foreground-400">
                漫画阅读区 · 韩漫网站
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => {
                if (chapterPage > 1) {
                  setChapterPage(chapterPage - 1);
                } else {
                  const currentIdx = comic.chapters.findIndex((c) => c.id === currentChapter.id);
                  if (currentIdx > 0) {
                    setCurrentChapter(comic.chapters[currentIdx - 1]);
                    setChapterPage(totalChapterPages);
                  }
                }
              }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-700 hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              上一页
            </button>

            <button
              onClick={() => setCurrentChapter(null)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-secondary-100 text-sm text-secondary-700 hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-go-back-line"></i>
              返回详情
            </button>

            <button
              onClick={() => {
                if (chapterPage < totalChapterPages) {
                  setChapterPage(chapterPage + 1);
                } else {
                  const currentIdx = comic.chapters.findIndex((c) => c.id === currentChapter.id);
                  if (currentIdx < comic.chapters.length - 1) {
                    setCurrentChapter(comic.chapters[currentIdx + 1]);
                    setChapterPage(1);
                  }
                }
              }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 text-white text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              下一页
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title={`${comic.title} - 韩国漫画免费在线阅读`}
        description={`在韩漫网站免费在线阅读《${comic.title}》，作者${comic.author}。${comic.description.substring(0, 80)}... 韩国漫画免费阅读尽在韩漫大全。`}
        keywords={`韩漫网站,韩国漫画免费,韩漫大全,${comic.title},${comic.author},${comic.category}漫画`}
        canonicalUrl={`https://hanman.example.com/article/${comic.id}`}
        ogType="article"
        ogImage={comic.cover}
        articlePublishedTime={comic.createdAt}
        articleModifiedTime={comic.updatedAt}
      />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: comic.category, href: `/category/${comic.categorySlug}` },
            { label: comic.title },
          ]}
        />

        <div className="flex flex-col lg:flex-row gap-8">
          <div className="lg:w-[300px] flex-shrink-0">
            <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-200 sticky top-24">
              <img
                src={comic.cover}
                alt={`${comic.title} 韩漫封面`}
                title={`${comic.title} - 韩国漫画免费在线观看`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                comic.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'
              }`}>
                {comic.status}
              </span>
              <span className="text-xs text-foreground-500">{comic.category}</span>
            </div>

            <h1 className="font-heading text-2xl md:text-3xl font-black text-foreground-950 mb-2">
              {comic.title}
            </h1>
            <p className="text-sm text-foreground-500 mb-4">
              作者：{comic.author} · 更新于 {comic.updatedAt}
            </p>

            <div className="flex flex-wrap items-center gap-4 mb-6">
              <div className="flex items-center gap-1.5">
                <span className="text-accent-500 font-bold text-lg">{comic.rating}</span>
                <div className="flex text-accent-500 text-sm">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i
                      key={star}
                      className={`${star <= Math.round(comic.rating / 2) ? 'ri-star-fill' : 'ri-star-line'}`}
                    ></i>
                  ))}
                </div>
              </div>
              <span className="text-sm text-foreground-500">
                <i className="ri-eye-line mr-1"></i>
                {(comic.views / 10000).toFixed(1)}万 阅读
              </span>
              <span className="text-sm text-foreground-500">
                <i className="ri-heart-line mr-1"></i>
                {(comic.likes / 10000).toFixed(1)}万 收藏
              </span>
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              {comic.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="bg-background-100 rounded-xl p-5 mb-8">
              <h3 className="font-heading text-sm font-bold text-foreground-900 mb-2">作品简介</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
            </div>

            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-bold text-foreground-950">
                  章节列表
                  <span className="text-sm font-normal text-foreground-500 ml-2">
                    共 {totalChapters} 话
                  </span>
                </h3>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-foreground-500">
                    {comic.status === '连载中' ? '正序' : '全 {totalChapters} 话'}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {comic.chapters.map((chapter) => (
                  <button
                    key={chapter.id}
                    onClick={() => {
                      setCurrentChapter(chapter);
                      setChapterPage(1);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="flex items-center justify-between px-4 py-2.5 rounded-lg bg-background-100 hover:bg-background-200 text-sm text-foreground-700 hover:text-primary-500 transition-all duration-200 cursor-pointer"
                  >
                    <span className="truncate">第{chapter.number}话</span>
                    <span className="text-xs text-foreground-400 ml-2 flex-shrink-0">{chapter.createdAt}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {relatedComics.length > 0 && (
          <section className="mt-12 pt-8 border-t border-background-200">
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5" data-product-shop>
              {relatedComics.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}